import api from '@/services/api';
import AlertDialog from '@/components/AlertDialog';
import Info from '@/components/Info';
import Card from '@/components/Card';
import FileList from './FileList';
import { CircularProgress, Typography } from '@mui/material';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import { CircleX, HardDriveUpload, Info as InfoIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  DescriptionContainer,
  NoContentContainer,
  TitleContainer,
} from './styles';
import { enqueueSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

/**
 * Displays the updates section on equipment page
 * @param equipment
 * @returns
 */
function Updates({ equipment }: { equipment: EquipmentType }) {
  const theme = useTheme();
  const { t } = useTranslation();
  const [files, setFiles] = useState<FileType[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<FileType | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [valueConfirm, setValueConfirm] = useState(false);

  useEffect(() => {
    setLoading(true);
    const fetchFiles = () => {
      api
        .get(`/models/${equipment.model.id}/files`)
        .then((response) => {
          if (response.status === 200) {
            setFiles(response.data);
          }
        })
        .catch((error) => {
          console.error('Erro ao buscar arquivos:', error);
        })
        .finally(() => setLoading(false));
    };

    fetchFiles();
  }, [equipment.model.id, confirmDelete]);

  useEffect(() => {
    if (!valueConfirm || !selectedFile) return;

    api
      .post(`/devices/${equipment.id}/files/${selectedFile.id}`, {
        fileId: selectedFile.id,
      })
      .then((response) => {
        if (response.status === 200) {
          enqueueSnackbar(t('equipment.updates.updatedSuccess'), {
            variant: 'success',
          });
        }
      })
      .catch((error) => {
        enqueueSnackbar(
          `${t('equipment.updates.updatedError')} ${error.message}`,
          {
            variant: 'error',
          }
        );
      })
      .finally(() => {
        setValueConfirm(false);
      });
  }, [valueConfirm, selectedFile, equipment.id, t]);

  return (
    <Stack>
      <Stack direction="row">
        <Typography variant="h3" textAlign="left">
          {t('equipment.updates.title')}
        </Typography>
        <Info
          icon={InfoIcon}
          placement="right"
          children={
            <DescriptionContainer>
              <Typography variant="caption" color={theme.palette.text.primary}>
                {t('equipment.updates.infoPlaceholderP1')}
                <HardDriveUpload size={24} color={theme.palette.primary.main} />
                {t('equipment.updates.infoPlaceholderP2')}
              </Typography>
            </DescriptionContainer>
          }
        />
      </Stack>
      {loading ? (
        <Card sx={{ maxWidth: '100%' }}>
          <NoContentContainer>
            <CircularProgress
              enableTrackSlot
              size="18px"
              sx={{ color: theme.palette.white['06'] }}
            />
            <Typography variant="caption" color={theme.palette.white['06']}>
              {t('equipment.connectedHosts.loading')}
            </Typography>
          </NoContentContainer>
        </Card>
      ) : files.length > 0 ? (
        <FileList
          files={files}
          selectedModelKey={equipment.model.key}
          setSelectedFile={setSelectedFile}
          setDialogFile={setOpenConfirm}
          setConfirmDelete={setConfirmDelete}
        />
      ) : (
        <Stack>
          <Stack alignItems="flex-start" width="100%" gap="24px">
            <TitleContainer>
              <Typography variant="h3" textAlign="left">
                {t('equipment.updates.files')}
              </Typography>
              <Typography
                variant="h5"
                color={theme.palette.primary.main}
              >{` ${equipment.model.key}`}</Typography>
            </TitleContainer>
            <Card>
              <NoContentContainer>
                <CircleX strokeWidth={1.5} size={30} />
                <Typography
                  fontWeight={400}
                  fontStyle="italic"
                  fontSize="18px"
                  color="currentColor"
                >
                  {t('equipment.updates.noFiles')}
                </Typography>
              </NoContentContainer>
            </Card>
          </Stack>
        </Stack>
      )}
      <AlertDialog
        title={`${t('equipment.updates.deviceUpdate')} ${
          equipment.model.alias
            ? `${equipment.model.alias} ${equipment.serialNumber}`
            : `${equipment.manufacturer} ${equipment.productClass}`
        }`}
        open={openConfirm}
        setOpen={setOpenConfirm}
        content={
          <Typography>{t('equipment.updates.confirmUpdate')}</Typography>
        }
        confirmText="Atualizar"
        cancelText="Cancelar"
        setValue={setValueConfirm}
      />
    </Stack>
  );
}

export default Updates;
